/* eslint-disable */
// Global search over the server (app-shell 2026-09-14, lever 5 / C2). The overlay used to fetch every
// item (its own `select *`, 9 pages / 6.9 MB) and read the App's full developments list; it now asks
// the server the four questions the in-memory matcher answered — the same fields, case-insensitive
// substring (globalSearchData.matchEntity: ITEM_FIELDS + bare shape for items; dev_number · designer ·
// fty · material · notes for developments; a designer's or a brand's name for those groups) — and
// gets back the rows the overlay shows (CAP per group) plus each group's true total (count=exact).
// Designers and brands are grouped from one tiny row per matching development / item, the way
// buildDesigners / buildBrands grouped the full lists. Order: newest first, id as the tiebreaker.
// Stated differences from the in-memory matcher: the query is stripped of PostgREST's reserved
// characters . , ( ) ' " \ (as the catalog search is); % and _ are LIKE wildcards; a brand bar's
// `max` is the largest count among the MATCHING brands, not among all of them.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../supabase';
import { fetchAllPaged } from '../../utils/pgIn';
import { ITEM_FIELDS } from './globalSearchData';
import {
  globalSearchKey, mapDeskOrder, mapBookOrder, mapAcetate, mergeOrderResults,
} from './globalSearchExtraData';

const DEV_FIELDS = ['dev_number', 'designer', 'fty', 'material', 'notes'];
const DEV_ROW = 'id,created_at,dev_number:data->dev_number,designer:data->designer,fty:data->fty,stage_status:data->stage_status,images:data->images';
export const clean = (q) => (q || '').replace(/[.,()'"\\]/g, '').trim().toLowerCase();
const orOf = (fields, s) => fields.map((f) => `data->>${f}.ilike.%${s}%`).join(',');
const titleCase = (s) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const newest = (q) => q.order('created_at', { ascending: false }).order('id');

export async function searchItemsGS(s, cap, signal) {
  let q = supabase.from('items').select('id,data', { count: 'exact' });
  if (s) q = q.or(orOf([...ITEM_FIELDS, 'shape'], s));
  const { data, count, error } = await newest(q).limit(cap).abortSignal(signal);
  if (error) throw error;
  return { rows: (data || []).map((r) => ({ ...(r.data || {}), id: r.id })), total: count ?? 0 };
}

export async function searchDevsGS(s, cap, signal) {
  let q = supabase.from('developments').select(DEV_ROW, { count: 'exact' }).is('deleted_at', null);
  if (s) q = q.or(orOf(DEV_FIELDS, s));
  const { data, count, error } = await newest(q).limit(cap).abortSignal(signal);
  if (error) throw error;
  return { rows: (data || []).map((r) => ({ ...r, images: Array.isArray(r.images) ? r.images : [] })), total: count ?? 0 };
}

// buildDesigners over the developments whose designer contains the query (all of them when it is empty)
export async function searchDesignersGS(s, signal) {
  const rows = await fetchAllPaged(() => { let q = supabase.from('developments').select('id,designer:data->>designer').is('deleted_at', null); if (s) q = q.ilike('data->>designer', `%${s}%`); return q.order('id').abortSignal(signal); }, { label: 'global-search designers' });
  const counts = {};
  for (const r of rows) { const raw = String(r.designer || '').trim(); if (!raw) continue; const name = titleCase(raw); counts[name] = (counts[name] || 0) + 1; }
  const max = Math.max(1, ...Object.values(counts));
  return Object.entries(counts).map(([name, devs]) => ({ name, devs, max })).sort((a, b) => b.devs - a.devs);
}

// buildBrands: the configured brand options whose name contains the query, each with its item count
export async function searchBrandsGS(s, fields, signal) {
  const opts = (((fields || []).find((f) => f.key === 'brand') || {}).options || []).filter((o) => !s || String(o).toLowerCase().includes(s));
  if (!opts.length) return [];
  const rows = await fetchAllPaged(() => { let q = supabase.from('items').select('id,brand:data->>brand'); if (s) q = q.ilike('data->>brand', `%${s}%`); return q.order('id').abortSignal(signal); }, { label: 'global-search brands' });
  const counts = {};
  for (const r of rows) if (r.brand) counts[r.brand] = (counts[r.brand] || 0) + 1;
  const max = Math.max(1, ...opts.map((o) => counts[o] || 0));
  return opts.map((name) => ({ name, items: counts[name] || 0, max })).sort((a, b) => b.items - a.items);
}

export async function searchOrdersGS(s, cap, signal) {
  const deskFields = ['pi_no', 'item_no', 'model_name', 'supplier_code', 'factory_code', 'collection_code', 'material'];
  const bookFields = ['pi', 'item_no', 'supplier', 'collection', 'material', 'dev_no'];
  const desk = supabase.from('orders_pis')
    .select('id,pi_no,item_no,model_name,supplier_code,factory_code,collection_code,material,status,order_date,created_at', { count: 'exact' })
    .or(deskFields.map((field) => `${field}.ilike.%${s}%`).join(','))
    .order('created_at', { ascending: false }).limit(cap).abortSignal(signal);
  const book = supabase.from('orders_lines')
    .select('id,pi,item_no,supplier,collection,material,status,order_status,order_date,created_at', { count: 'exact' })
    .or(bookFields.map((field) => `${field}.ilike.%${s}%`).join(','))
    .order('created_at', { ascending: false }).limit(cap).abortSignal(signal);
  const [deskRes, bookRes] = await Promise.all([desk, book]);
  if (deskRes.error) throw deskRes.error;
  if (bookRes.error) throw bookRes.error;
  const deskRows = (deskRes.data || []).map(mapDeskOrder);
  const bookRows = (bookRes.data || []).map(mapBookOrder);
  return {
    rows: mergeOrderResults(deskRows, bookRows, cap),
    total: (deskRes.count ?? deskRows.length) + (bookRes.count ?? bookRows.length),
  };
}

export async function searchAcetatesGS(s, cap, signal) {
  const select = 'id,acetate_no,colour_name,colour_family,finish,swatch_a,swatch_b';
  const bank = supabase.from('acetate_bank').select(select, { count: 'exact' })
    .is('deleted_at', null)
    .or(['acetate_no', 'colour_name', 'colour_family', 'finish'].map((field) => `${field}.ilike.%${s}%`).join(','))
    .order('acetate_no').limit(cap).abortSignal(signal);
  const aliases = supabase.from('acetate_alias').select('acetate_no,alias_raw,alias_norm')
    .or(`alias_raw.ilike.%${s}%,alias_norm.ilike.%${s}%`)
    .not('acetate_no', 'is', null).limit(cap * 4).abortSignal(signal);
  const [bankRes, aliasRes] = await Promise.all([bank, aliases]);
  if (bankRes.error) throw bankRes.error;
  if (aliasRes.error) throw aliasRes.error;

  const aliasByCode = new Map();
  (aliasRes.data || []).forEach((row) => {
    if (!aliasByCode.has(row.acetate_no)) aliasByCode.set(row.acetate_no, []);
    aliasByCode.get(row.acetate_no).push(row.alias_raw || row.alias_norm);
  });
  const direct = bankRes.data || [];
  const directCodes = new Set(direct.map((row) => row.acetate_no));
  const missingCodes = [...aliasByCode.keys()].filter((code) => !directCodes.has(code));
  let aliasBank = [];
  if (missingCodes.length) {
    const { data, error } = await supabase.from('acetate_bank').select(select)
      .is('deleted_at', null).in('acetate_no', missingCodes).limit(cap).abortSignal(signal);
    if (error) throw error;
    aliasBank = data || [];
  }
  const rows = [...direct, ...aliasBank]
    .map((row) => mapAcetate(row, aliasByCode.get(row.acetate_no) || []))
    .slice(0, cap);
  return { rows, total: (bankRes.count ?? direct.length) + aliasBank.length };
}

const OPTS = { staleTime: 60 * 1000, retry: 0 };
const GROUP_OPTS = { staleTime: 5 * 60 * 1000, retry: 0 };
/** `q` is the DEBOUNCED query; `cap` the rows the overlay shows per group. */
export function useGlobalSearchServer({ q, cap, fields, userId, enabled, canCatalog, canDev, canOrders, canAcetate }) {
  const s = clean(q);
  const items = useQuery({ queryKey: globalSearchKey('items', userId, s, cap), queryFn: ({ signal }) => searchItemsGS(s, cap, signal), enabled: enabled && canCatalog, ...OPTS });
  const devs = useQuery({ queryKey: globalSearchKey('devs', userId, s, cap), queryFn: ({ signal }) => searchDevsGS(s, cap, signal), enabled: enabled && canDev, ...OPTS });
  const designers = useQuery({ queryKey: globalSearchKey('designers', userId, s, cap), queryFn: ({ signal }) => searchDesignersGS(s, signal), enabled: enabled && canDev, ...GROUP_OPTS });
  const brands = useQuery({ queryKey: [...globalSearchKey('brands', userId, s, cap), (fields || []).length], queryFn: ({ signal }) => searchBrandsGS(s, fields, signal), enabled: enabled && canCatalog, ...GROUP_OPTS });
  const orders = useQuery({ queryKey: globalSearchKey('orders', userId, s, cap), queryFn: ({ signal }) => searchOrdersGS(s, cap, signal), enabled: enabled && canOrders, ...OPTS });
  const acetates = useQuery({ queryKey: globalSearchKey('acetates', userId, s, cap), queryFn: ({ signal }) => searchAcetatesGS(s, cap, signal), enabled: enabled && canAcetate, ...OPTS });
  const showCatalog = enabled && canCatalog;
  const showDev = enabled && canDev;
  const showOrders = enabled && canOrders;
  const showAcetate = enabled && canAcetate;
  const active = [showCatalog && items, showDev && devs, showDev && designers, showCatalog && brands, showOrders && orders, showAcetate && acetates].filter(Boolean);
  return {
    items: showCatalog && !items.isError ? items.data?.rows || EMPTY : EMPTY, itemsTotal: showCatalog ? items.data?.total || 0 : 0,
    devs: showDev && !devs.isError ? devs.data?.rows || EMPTY : EMPTY, devsTotal: showDev ? devs.data?.total || 0 : 0,
    designers: showDev && !designers.isError ? designers.data || EMPTY : EMPTY, brands: showCatalog && !brands.isError ? brands.data || EMPTY : EMPTY,
    orders: showOrders && !orders.isError ? orders.data?.rows || EMPTY : EMPTY, ordersTotal: showOrders ? orders.data?.total || 0 : 0,
    acetates: showAcetate && !acetates.isError ? acetates.data?.rows || EMPTY : EMPTY, acetatesTotal: showAcetate ? acetates.data?.total || 0 : 0,
    loading: active.some((query) => query.isFetching),
    error: active.find((query) => query.error)?.error || null,
  };
}
const EMPTY = [];
